/**
 * F39 (Wave 2C) — admin placement analytics.
 *
 * Route: `/placements/analytics`
 * Permission: `placement_inventory:read` (super_admin + support + finance +
 *             content_moderator).
 *
 * Platform-wide performance surface: a KPI row (impressions / clicks /
 * conversions / CTR / CPC / CPA / revenue), a Bar chart of revenue by placement
 * type, a Bar chart of fill rate by scope, and a top-venues-by-spend Table.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chart,
  CurrencyDisplay,
  EmptyState,
  Skeleton,
  Stat,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { adminPlacementsApi, type AdminPlacementAnalyticsResponse } from '../../api/placements';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';

type TopVenueRow = AdminPlacementAnalyticsResponse['topVenues'][number];

function formatMoney(minor: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

export function PlacementAnalyticsPage() {
  const { t, locale } = useI18n();

  const [data, setData] = useState<AdminPlacementAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminPlacementsApi.analytics());
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData();
  }, [fetchData]);

  const revenueChartData = useMemo(
    () =>
      (data?.revenueByType ?? []).map((r) => ({
        type: t(`placement:type.${r.placementType}`, { defaultValue: r.placementType }),
        revenue: r.revenueMinor / 100,
      })),
    [data, t],
  );

  const fillRateChartData = useMemo(
    () =>
      (data?.fillRateByScope ?? []).map((r) => ({
        scope: t(`placement:scope_kind.${r.scopeKind}`, { defaultValue: r.scopeKind }),
        fillRate: Math.round(r.fillRate * 100),
      })),
    [data, t],
  );

  const topVenueColumns = useMemo<readonly TableColumn<TopVenueRow>[]>(
    () => [
      {
        key: 'venue',
        header: t('placement:admin.analytics.col_venue', { defaultValue: 'Venue' }),
        cell: (row) => <span className="text-[13px] text-ink">{row.venueName ?? row.venueId}</span>,
      },
      {
        key: 'placements',
        header: t('placement:admin.analytics.col_placements', { defaultValue: 'Placements' }),
        align: 'end',
        cell: (row) => (
          <span className="text-[12px] text-ink-2 tabular-nums">{row.placements}</span>
        ),
      },
      {
        key: 'spend',
        header: t('placement:admin.analytics.col_spend', { defaultValue: 'Spend' }),
        align: 'end',
        cell: (row) => <CurrencyDisplay amount={row.spendMinor} currency="USD" locale={locale} />,
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="placement-analytics-page">
      <PageHeader
        title={t('placement:admin.analytics.title', { defaultValue: 'Placement analytics' })}
        subtitle={t('placement:admin.analytics.subtitle', {
          defaultValue: 'Platform-wide placement performance: revenue, fill rate, and the funnel.',
        })}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="placement-analytics-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : !data ? (
        <EmptyState
          title={t('placement:admin.analytics.empty', {
            defaultValue: 'No placement analytics yet.',
          })}
        />
      ) : (
        <>
          {/* KPI row */}
          <section
            className="grid grid-cols-2 gap-3 md:grid-cols-4"
            data-testid="placement-analytics-kpis"
          >
            <Stat
              label={t('placement:admin.analytics.impressions', { defaultValue: 'Impressions' })}
              value={data.totals.impressions.toLocaleString(locale)}
            />
            <Stat
              label={t('placement:admin.analytics.clicks', { defaultValue: 'Clicks' })}
              value={data.totals.clicks.toLocaleString(locale)}
            />
            <Stat
              label={t('placement:admin.analytics.conversions', { defaultValue: 'Conversions' })}
              value={data.totals.conversions.toLocaleString(locale)}
            />
            <Stat
              label={t('placement:admin.analytics.revenue', { defaultValue: 'Revenue' })}
              value={formatMoney(data.totals.revenueMinor, data.totals.currency, locale)}
            />
            <Stat
              label={t('placement:admin.analytics.ctr', { defaultValue: 'CTR' })}
              value={`${(data.totals.ctr * 100).toFixed(1)}%`}
            />
            <Stat
              label={t('placement:admin.analytics.cpc', { defaultValue: 'CPC' })}
              value={formatMoney(data.totals.cpcMinor, data.totals.currency, locale)}
            />
            <Stat
              label={t('placement:admin.analytics.cpa', { defaultValue: 'CPA' })}
              value={formatMoney(data.totals.cpaMinor, data.totals.currency, locale)}
            />
          </section>

          {/* Revenue by type */}
          <section className="space-y-2" data-testid="placement-analytics-revenue-by-type">
            <Chart
              kind="bar"
              data={revenueChartData}
              xKey="type"
              series={[
                {
                  key: 'revenue',
                  label: t('placement:admin.analytics.revenue_by_type', {
                    defaultValue: 'Revenue by type',
                  }),
                },
              ]}
              title={t('placement:admin.analytics.revenue_by_type', {
                defaultValue: 'Revenue by type',
              })}
              height={260}
            />
          </section>

          {/* Fill rate by scope */}
          <section className="space-y-2" data-testid="placement-analytics-fill-rate">
            <Chart
              kind="bar"
              data={fillRateChartData}
              xKey="scope"
              series={[
                {
                  key: 'fillRate',
                  label: t('placement:admin.analytics.fill_rate', {
                    defaultValue: 'Fill rate (%)',
                  }),
                },
              ]}
              title={t('placement:admin.analytics.fill_rate', {
                defaultValue: 'Fill rate by scope (%)',
              })}
              height={240}
            />
          </section>

          {/* Top venues */}
          <section className="space-y-3" data-testid="placement-analytics-top-venues">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('placement:admin.analytics.top_venues', { defaultValue: 'Top venues by spend' })}
            </h2>
            {data.topVenues.length === 0 ? (
              <EmptyState
                title={t('placement:admin.analytics.top_venues_empty', {
                  defaultValue: 'No venue spend yet.',
                })}
              />
            ) : (
              <Table
                data={data.topVenues as TopVenueRow[]}
                columns={topVenueColumns as TableColumn<TopVenueRow>[]}
                rowKey={(row) => row.venueId}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default PlacementAnalyticsPage;
